import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { notify } from "../lib/notify";
import DashboardLayout from "../components/DashboardLayout";
import { formatOrdinalDate } from "../lib/timeFormat";
import {
  Link2, Copy, Send, Trash2, Users, CheckCircle2, ExternalLink, ArrowLeft,
  Pause, Play, UserPlus, Upload, Mail, Clock, Pencil, X,
} from "lucide-react";

export default function GuestListDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [invitees, setInvitees] = useState(null);
  const [inviteSummary, setInviteSummary] = useState(null);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [toggling, setToggling] = useState(false);
  const [sending, setSending] = useState(false);
  const [entries, setEntries] = useState(null);
  const [csvMap, setCsvMap] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [manageSig, setManageSig] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);

  const load = () => api.get(`/guest-lists/${id}`).then((r) => setData(r.data)).catch(() => notify.error("Not found"));
  const loadInvitees = () => api.get(`/guest-lists/${id}/invitees`).then((r) => { setInvitees(r.data.invitees); setInviteSummary(r.data.summary); }).catch(() => setInvitees([]));
  const loadEntries = () => api.get(`/guest-lists/${id}/entries`).then((r) => setEntries(r.data.groups)).catch(() => setEntries([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadInvitees(); loadEntries(); }, [id]);

  if (!data) return <DashboardLayout title="Guest list"><div className="text-sm text-[#8a8a8a] py-10 text-center">Loading…</div></DashboardLayout>;

  const gl = data.guest_list;
  const inviteLink = (code) => `${window.location.origin}/guest/${gl.token}?code=${code}`;

  const copy = (text, msg) => {
    (async () => { try { await navigator.clipboard.writeText(text); notify.success(msg); } catch { notify.error("Copy failed — select and copy manually"); } })();
  };
  const toggleStatus = async () => {
    if (toggling) return;
    setToggling(true);
    const status = gl.status === "active" ? "paused" : "active";
    try { await api.patch(`/guest-lists/${id}`, { status }); notify.success(status === "active" ? "Live" : "Paused"); await load(); }
    catch { notify.error("Failed"); } finally { setToggling(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this guest list? Tickets already claimed will still work.")) return;
    try { await api.delete(`/guest-lists/${id}`); notify.success("Deleted"); nav("/dashboard/guest-lists"); }
    catch { notify.error("Failed"); }
  };
  const addInvitee = async () => {
    if (!newEmail.trim()) return notify.error("Enter an email");
    try {
      const r = await api.post(`/guest-lists/${id}/invitees`, { name: newName.trim(), email: newEmail.trim() });
      setNewName(""); setNewEmail("");
      notify.success(r.data.duplicate ? "Already on the invite list" : "Invitee added");
      loadInvitees(); load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  const removeInvitee = async (inv) => {
    if (!window.confirm(`Remove ${inv.email} from the invite list?`)) return;
    try { await api.delete(`/guest-lists/${id}/invitees/${inv.id}`); notify.success("Removed"); loadInvitees(); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  const broadcastAll = async () => {
    const n = inviteSummary?.accepted || 0;
    if (!n) return notify.error("No accepted guests to email yet");
    if (!window.confirm(`Send the guest-list broadcast email to ${n} accepted guest${n === 1 ? "" : "s"} on this list?`)) return;
    setBroadcasting(true);
    try {
      const r = await api.post(`/guest-lists/${id}/broadcast`);
      notify.success(`Emailed ${r.data.recipients} guest${r.data.recipients === 1 ? "" : "s"}${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed to send"); }
    finally { setBroadcasting(false); }
  };
  const resendTickets = async (inv) => {
    try { await api.post(`/guest-lists/${id}/invitees/${inv.id}/resend-tickets`); notify.success(`Tickets re-sent to ${inv.email}`); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  const sendAll = async (invite_ids = null) => {
    setSending(true);
    if (invite_ids) setInvitees((prev) => (prev || []).map((i) => invite_ids.includes(i.id) ? { ...i, invited_at: new Date().toISOString() } : i));
    try {
      const r = await api.post(`/guest-lists/${id}/invitees/send`, invite_ids ? { invite_ids } : {});
      notify.success(`${r.data.sent} invite(s) sent`); loadInvitees();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); loadInvitees(); }
    finally { setSending(false); }
  };
  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsvFull(text, gl.custom_fields || []);
    if (parsed.records.length === 0) return notify.error("No valid rows found in CSV (need an email column)");
    const customs = gl.custom_fields || [];
    if (customs.length > 0 && parsed.extraHeaders.length > 0) {
      setCsvMap(parsed);   // open mapping modal
    } else {
      await uploadInvitees(parsed, {});
    }
  };

  const uploadInvitees = async (parsed, mapping, updateExisting = false) => {
    const invitees = parsed.records.map((rec) => {
      const cf = {};
      for (const [key, header] of Object.entries(mapping)) {
        if (header && rec[header]) cf[key] = rec[header];
      }
      const email = rec[parsed.emailHeader] || "";
      const name = (parsed.nameHeader && rec[parsed.nameHeader]) || "";
      return { name: name === email ? "" : name, email, custom_fields: cf };
    });
    try {
      const r = await api.post(`/guest-lists/${id}/invitees/bulk`, { invitees, update_existing: updateExisting });
      const parts = [];
      if (r.data.added) parts.push(`${r.data.added} added`);
      if (r.data.updated) parts.push(`${r.data.updated} updated`);
      if ((r.data.skipped || 0) + parsed.dropped) parts.push(`${(r.data.skipped || 0) + parsed.dropped} skipped`);
      notify.success(parts.join(", ") || "Done");
      setCsvMap(null); loadInvitees(); load();
    } catch (err) { notify.error(formatErr(err.response?.data?.detail) || "Upload failed"); }
  };

  const toggleSel = (id2) => setSel((prev) => { const n = new Set(prev); n.has(id2) ? n.delete(id2) : n.add(id2); return n; });
  const toggleSelAll = () => setSel((prev) => (prev.size === (invitees || []).length ? new Set() : new Set((invitees || []).map((i) => i.id))));
  const clearSel = () => setSel(new Set());
  const bulkSend = async () => {
    setSending(true);
    try { const r = await api.post(`/guest-lists/${id}/invitees/send`, { invite_ids: [...sel] }); notify.success(`${r.data.sent} invite(s) sent`); clearSel(); loadInvitees(); }
    catch (e) { notify.error("Failed"); } finally { setSending(false); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Remove ${sel.size} selected invitee(s)? (Accepted ones are kept.)`)) return;
    try {
      const r = await api.post(`/guest-lists/${id}/invitees/delete`, { invite_ids: [...sel] });
      notify.success(`${r.data.deleted} removed${r.data.skipped ? `, ${r.data.skipped} kept (accepted)` : ""}`);
      clearSel(); loadInvitees(); load();
    } catch (e) { notify.error("Failed"); }
  };

  const pending = (invitees || []).filter((i) => i.status === "invited").length;

  return (
    <DashboardLayout
      title={gl.title}
      subtitle={`${data.event?.name || ""} · ${gl.date_ids?.length || 0} dates`}
      actions={
        <div className="flex gap-2 items-center">
          <button className="st-btn st-btn-ghost" onClick={() => nav("/dashboard/guest-lists")} data-testid="back-btn"><ArrowLeft size={14} /> All lists</button>
          <button className="st-btn st-btn-ghost" onClick={toggleStatus} disabled={toggling} data-testid="toggle-status-btn">
            {gl.status === "active" ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Go live</>}
          </button>
        </div>
      }
    >
      {/* summary chips */}
      {inviteSummary && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="invite-summary">
          <Stat label="Invited" value={inviteSummary.total} icon={Mail} />
          <Stat label="Accepted" value={inviteSummary.accepted} icon={CheckCircle2} accent />
          <Stat label="Guests coming" value={inviteSummary.guests_coming} icon={Users} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Invitees */}
          <div className="st-card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Invite list</h3>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} data-testid="csv-input" />
                <button className="st-btn st-btn-ghost text-xs" onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn"><Upload size={13} /> Upload CSV</button>
                <button className="st-btn text-xs" onClick={() => sendAll()} disabled={sending || pending === 0} data-testid="send-all-btn"><Send size={13} /> {sending ? "Sending…" : `Send ${pending} pending`}</button>
              </div>
            </div>

            {/* add one */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <input className="st-input flex-1 min-w-[120px]" placeholder="Name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="invitee-name-input" />
              <input className="st-input flex-1 min-w-[160px]" placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addInvitee()} data-testid="invitee-email-input" />
              <button className="st-btn" onClick={addInvitee} data-testid="add-invitee-btn"><UserPlus size={14} /> Add</button>
            </div>

            {invitees === null ? <div className="text-sm text-[#8a8a8a] py-6 text-center">Loading…</div> : invitees.length === 0 ? (
              <p className="text-sm text-[#8a8a8a] py-6 text-center" data-testid="no-invitees">No invitees yet. Add someone or upload a CSV (columns: name, email).</p>
            ) : (
              <div className="overflow-x-auto">
                {sel.size > 0 && (
                  <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/30 text-sm" data-testid="bulk-action-bar">
                    <span className="text-[#fca5a5] font-medium">{sel.size} selected</span>
                    <div className="ml-auto flex gap-2">
                      <button className="st-btn st-btn-ghost text-xs" onClick={bulkSend} disabled={sending} data-testid="bulk-send-btn"><Send size={12} /> Send invites</button>
                      <button className="st-btn st-btn-ghost text-xs text-[#dc2626]" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 size={12} /> Delete</button>
                      <button className="st-btn st-btn-ghost text-xs" onClick={clearSel} data-testid="bulk-clear-btn">Clear</button>
                    </div>
                  </div>
                )}
                <table className="w-full text-sm" data-testid="invitees-table">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
                    <tr>
                      <th className="py-2 w-8"><input type="checkbox" className="accent-[#dc2626]" ref={(el) => { if (el) el.indeterminate = sel.size > 0 && sel.size < invitees.length; }} checked={sel.size > 0 && sel.size === invitees.length} onChange={toggleSelAll} data-testid="select-all-invitees" /></th>
                      <th className="py-2">Invitee</th><th>Code</th><th>Status</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitees.map((inv) => (
                      <tr key={inv.id} className={`border-t border-[#161616] ${sel.has(inv.id) ? "bg-[#dc2626]/5" : ""}`} data-testid={`invitee-row-${inv.id}`}>
                        <td className="align-top pt-3"><input type="checkbox" className="accent-[#dc2626]" checked={sel.has(inv.id)} onChange={() => toggleSel(inv.id)} data-testid={`select-invitee-${inv.id}`} /></td>
                        <td className="py-2.5">
                          <div className="font-medium">{inv.name || "—"}</div>
                          <div className="text-xs text-[#8a8a8a]">{inv.email}</div>
                          {inv.custom_fields && Object.keys(inv.custom_fields).length > 0 && (
                            <div className="text-xs text-[#7a7a7a] mt-0.5">
                              {Object.values(inv.custom_fields).map((v, idx) => <span key={idx} className="mr-2">{v}</span>)}
                            </div>
                          )}
                        </td>
                        <td><span className="st-mono text-xs text-[#dc2626]">{inv.code}</span></td>
                        <td>
                          {inv.status === "accepted" ? (
                            <span className="st-chip st-chip-green" title={inv.event_date ? formatOrdinalDate(inv.event_date) : ""}>
                              <CheckCircle2 size={11} className="inline" /> Accepted · {inv.party_size} {inv.party_size === 1 ? "ticket" : "tickets"}
                            </span>
                          ) : inv.invited_at ? (
                            <span className="st-chip"><Clock size={11} className="inline" /> Invited</span>
                          ) : (
                            <span className="st-chip">Not sent</span>
                          )}
                        </td>
                        <td className="text-right whitespace-nowrap">
                          <button className="st-btn st-btn-ghost text-xs py-1" onClick={() => copy(inviteLink(inv.code), "Invite link copied")} data-testid={`copy-invite-${inv.id}`}><Copy size={12} /></button>
                          {inv.status !== "accepted" && <button className="st-btn st-btn-ghost text-xs py-1 ml-1" onClick={() => sendAll([inv.id])} data-testid={`resend-invite-${inv.id}`}><Send size={12} /></button>}
                          {inv.status === "accepted" && inv.order_id && <a className="st-btn st-btn-ghost text-xs py-1 ml-1" href={`/order/${inv.order_id}/tickets`} target="_blank" rel="noreferrer" data-testid={`invitee-tickets-${inv.id}`} title="View tickets"><ExternalLink size={12} /></a>}
                          {inv.status === "accepted" && <button className="st-btn st-btn-ghost text-xs py-1 ml-1" onClick={() => resendTickets(inv)} data-testid={`resend-tickets-${inv.id}`} title="Resend tickets email"><Mail size={12} /></button>}
                          {inv.status !== "accepted" && <button className="st-btn st-btn-ghost text-xs py-1 ml-1 text-[#dc2626]" onClick={() => removeInvitee(inv)} data-testid={`remove-invite-${inv.id}`}><Trash2 size={12} /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Who's expected */}
          <div className="st-card p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Who's expected each night</h3>
              <button
                className="st-btn text-xs"
                onClick={broadcastAll}
                disabled={broadcasting || !(inviteSummary?.accepted > 0)}
                data-testid="email-all-guests-btn"
                title={inviteSummary?.accepted ? "" : "No accepted guests to email yet"}
              >
                <Mail size={12} /> {broadcasting ? "Sending…" : "Email all guests"}
              </button>
            </div>
            {entries === null ? <div className="text-sm text-[#8a8a8a] py-6 text-center">Loading…</div> : entries.length === 0 ? (
              <p className="text-sm text-[#8a8a8a] py-6 text-center" data-testid="no-entries">No accepted guests yet.</p>
            ) : (
              <div className="space-y-5" data-testid="entries-list">
                {entries.map((g) => (
                  <div key={g.date_id}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold">{formatOrdinalDate(g.date)}</div>
                      <div className="text-xs text-[#8a8a8a]"><Users size={12} className="inline mr-1" />{g.guests} guests · {g.checked_in} in</div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {g.signups.map((s) => (
                          <tr key={s.order.id} className="border-t border-[#161616]">
                            <td className="py-2">
                              <div className="font-medium">{s.order.customer_name}</div>
                              <div className="text-xs text-[#8a8a8a]">{s.order.customer_email}</div>
                            </td>
                            <td className="text-right whitespace-nowrap">
                              <span className="st-chip">{s.party_size} {s.party_size === 1 ? "ticket" : "tickets"}</span>
                            </td>
                            <td className="text-right pl-2 whitespace-nowrap">
                              <button className="st-btn st-btn-ghost text-xs py-1" onClick={() => setManageSig({ signup: s })} data-testid={`manage-signup-${s.order.id}`}><Pencil size={12} /> Manage</button>
                              <a className="st-link inline-flex items-center gap-1 text-xs ml-2" href={`/order/${s.order.id}/tickets`} target="_blank" rel="noreferrer">Tickets <ExternalLink size={11} /></a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">How it works</h3>
            <p className="text-xs text-[#8a8a8a] leading-relaxed">
              This list is <strong className="text-[#bbb]">invite-only</strong>. Add people (or upload a CSV), then send invites — each person gets a unique link and code. Only invited people can claim tickets. To send someone manually, share their link or give them their code to enter on the sign-up page.
            </p>
          </div>

          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Sign-up page</h3>
            <div className="flex items-center gap-2">
              <input readOnly className="st-input text-xs flex-1" value={`${window.location.origin}/guest/${gl.token}`} data-testid="public-link-input" />
              <button className="st-btn st-btn-ghost" onClick={() => copy(`${window.location.origin}/guest/${gl.token}`, "Link copied")} data-testid="copy-public-link-btn"><Copy size={14} /></button>
            </div>
            <p className="text-[11px] text-[#7a7a7a] mt-2">People opening this without a code will be asked to enter their invite code.</p>
          </div>

          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Details</h3>
            <div className="text-sm space-y-1">
              <div className="flex justify-between items-center gap-2">
                <span className="text-[#8a8a8a]">Max additional guests</span>
                <MaxGuestsEditor value={gl.max_additional_guests} onSave={async (v) => { await api.patch(`/guest-lists/${id}`, { max_additional_guests: v }); notify.success("Updated"); await load(); }} />
              </div>
              <div className="flex justify-between"><span className="text-[#8a8a8a]">Custom fields</span><span>{gl.custom_fields?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-[#8a8a8a]">Status</span><span className={gl.status === "active" ? "text-[#86efac]" : "text-[#8a8a8a]"}>{gl.status}</span></div>
            </div>
            <button className="st-btn st-btn-ghost w-full mt-4 text-[#dc2626]" onClick={del} data-testid="delete-guest-list-btn"><Trash2 size={14} /> Delete guest list</button>
          </div>
        </div>
      </div>

      {csvMap && (
        <CsvMapModal
          parsed={csvMap}
          customFields={gl.custom_fields || []}
          onCancel={() => setCsvMap(null)}
          onConfirm={(mapping, updateExisting) => uploadInvitees(csvMap, mapping, updateExisting)}
        />
      )}

      {manageSig && (
        <ManageSignupModal
          glId={id}
          signup={manageSig.signup}
          dates={data.dates || []}
          maxAdditional={gl.max_additional_guests || 0}
          customFields={gl.custom_fields || []}
          onClose={() => setManageSig(null)}
          onSaved={() => { setManageSig(null); load(); loadInvitees(); loadEntries(); }}
        />
      )}
    </DashboardLayout>
  );
}

function MaxGuestsEditor({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setV(value); }, [value]);
  if (!editing) {
    return (
      <button className="inline-flex items-center gap-1.5 hover:text-[#dc2626]" onClick={() => setEditing(true)} data-testid="edit-max-guests-btn">
        <span>{value}</span><Pencil size={12} className="text-[#7a7a7a]" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min={0} max={50} className="st-input w-16 py-1 text-sm" value={v}
             onChange={(e) => setV(e.target.value)} data-testid="max-guests-input" />
      <button className="st-btn text-xs py-1" disabled={saving} data-testid="save-max-guests-btn"
              onClick={async () => { setSaving(true); try { await onSave(Math.max(0, parseInt(v || 0, 10))); setEditing(false); } finally { setSaving(false); } }}>Save</button>
      <button className="st-btn st-btn-ghost text-xs py-1" onClick={() => { setV(value); setEditing(false); }}>Cancel</button>
    </span>
  );
}

function ManageSignupModal({ glId, signup, dates, maxAdditional, customFields, onClose, onSaved }) {
  const o = signup.order;
  const [name, setName] = useState(o.customer_name || "");
  const [email, setEmail] = useState(o.customer_email || "");
  const [dateId, setDateId] = useState(o.event_date_id || "");
  const [party, setParty] = useState(signup.party_size || 1);
  const [cf, setCf] = useState(() => ({ ...(o.guest_custom_fields || {}) }));
  const [resend, setResend] = useState(true);
  const [saving, setSaving] = useState(false);
  const checkedIn = signup.checked_in || 0;
  const maxParty = 1 + Number(maxAdditional || 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, email, event_date_id: dateId, party_size: Number(party), custom_fields: cf, resend };
      const r = await api.patch(`/guest-lists/${glId}/signups/${o.id}`, payload);
      notify.success(r.data.resent ? "Saved & tickets re-sent" : "Saved");
      onSaved();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto st-fade-up" data-testid="manage-signup-modal">
      <div className="st-card p-6 w-full max-w-md my-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Manage guest booking</h2>
          <button className="text-[#8a8a8a] hover:text-white" onClick={onClose} data-testid="manage-close"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-[#8a8a8a]">Guest name</span>
            <input className="st-input w-full mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="manage-name" />
          </label>
          <label className="block">
            <span className="text-xs text-[#8a8a8a]">Email</span>
            <input className="st-input w-full mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="manage-email" />
          </label>
          <label className="block">
            <span className="text-xs text-[#8a8a8a]">Date</span>
            <select className="st-input w-full mt-1" value={dateId} onChange={(e) => setDateId(e.target.value)} data-testid="manage-date">
              {dates.map((d) => <option key={d.id} value={d.id}>{formatOrdinalDate(d.date)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-[#8a8a8a]">Tickets (party size)</span>
            <select className="st-input w-full mt-1" value={party} onChange={(e) => setParty(e.target.value)} data-testid="manage-party">
              {Array.from({ length: maxParty }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n} disabled={n < checkedIn}>{n} {n === 1 ? "ticket" : "tickets"}{n < checkedIn ? " (checked in)" : ""}</option>
              ))}
            </select>
            <span className="text-[11px] text-[#7a7a7a]">Max {maxParty} on this list{checkedIn > 0 ? ` · ${checkedIn} already checked in` : ""}. Raise the list's limit to allow more.</span>
          </label>

          {customFields.map((f) => (
            <label key={f.key} className="block" data-testid={`manage-cf-${f.key}`}>
              <span className="text-xs text-[#8a8a8a]">{f.label}{f.required ? " *" : ""}</span>
              <input className="st-input w-full mt-1" value={cf[f.key] || ""} onChange={(e) => setCf((p) => ({ ...p, [f.key]: e.target.value }))} />
            </label>
          ))}

          <label className="flex items-center gap-2 text-sm cursor-pointer pt-1" data-testid="manage-resend-toggle">
            <input type="checkbox" className="accent-[#dc2626]" checked={resend} onChange={(e) => setResend(e.target.checked)} />
            <span>Re-send updated tickets email to guest</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button className="st-btn st-btn-ghost" onClick={onClose} data-testid="manage-cancel">Cancel</button>
          <button className="st-btn" onClick={save} disabled={saving} data-testid="manage-save">{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="st-card p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? "bg-[#0c1d12] text-[#86efac]" : "bg-[#141414] text-[#dc2626]"}`}><Icon size={16} /></div>
      <div>
        <div className="text-lg font-bold leading-none">{value}</div>
        <div className="text-xs text-[#8a8a8a] mt-1">{label}</div>
      </div>
    </div>
  );
}

function parseCsvFull(text, customFields) {
  const empty = { headers: [], records: [], nameHeader: null, emailHeader: null, extraHeaders: [], dropped: 0 };
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return empty;
  const split = (l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const first = split(lines[0]);
  const lower = first.map((h) => h.toLowerCase());
  const hasHeader = lower.some((h) => h.includes("email"));
  let headers;
  let start;
  if (hasHeader) { headers = first.map((h, i) => h || `column_${i + 1}`); start = 1; }
  else { headers = first.map((_, i) => (i === 0 ? "name" : i === 1 ? "email" : `column_${i + 1}`)); start = 0; }
  const hl = headers.map((h) => h.toLowerCase());
  const customLabels = new Set((customFields || []).map((f) => (f.label || "").toLowerCase()));

  let emailIdx = hl.findIndex((h) => h.includes("email"));
  if (emailIdx < 0) emailIdx = headers.length > 1 ? 1 : 0;
  const nameCandidates = ["name", "full name", "fullname", "first name"];
  let nameIdx = hl.findIndex((h, i) => i !== emailIdx && !customLabels.has(h) && (nameCandidates.includes(h) || h.includes("name")));
  if (nameIdx < 0 && !hasHeader) nameIdx = 0 === emailIdx ? 1 : 0;

  const emailHeader = headers[emailIdx];
  const nameHeader = nameIdx >= 0 ? headers[nameIdx] : null;
  const extraHeaders = headers.filter((h, i) => i !== emailIdx && i !== nameIdx);

  const records = [];
  let dropped = 0;
  for (let i = start; i < lines.length; i++) {
    const cols = split(lines[i]);
    const rec = {};
    headers.forEach((h, idx) => { rec[h] = (cols[idx] || "").trim(); });
    const email = rec[emailHeader] || "";
    if (email && email.includes("@")) records.push(rec);
    else dropped += 1;
  }
  return { headers, records, nameHeader, emailHeader, extraHeaders, dropped };
}

function CsvMapModal({ parsed, customFields, onCancel, onConfirm }) {
  const autoMap = () => {
    const m = {};
    for (const f of customFields) {
      const hit = parsed.extraHeaders.find((h) => h.toLowerCase() === (f.label || "").toLowerCase());
      m[f.key] = hit || "";
    }
    return m;
  };
  const [mapping, setMapping] = useState(autoMap);
  const [updateExisting, setUpdateExisting] = useState(true);
  const preview = parsed.records[0] || {};

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto st-fade-up" data-testid="csv-map-modal">
      <div className="st-card p-6 w-full max-w-lg my-auto">
        <h2 className="text-lg font-bold">Map your columns</h2>
        <p className="text-sm text-[#8a8a8a] mt-1 mb-4">
          We found {parsed.records.length} contact{parsed.records.length === 1 ? "" : "s"} and extra columns. Match them to your custom fields so they pre-populate for each invitee.
        </p>
        <div className="space-y-3">
          {customFields.map((f) => (
            <div key={f.key} className="flex items-center gap-3" data-testid={`csv-map-row-${f.key}`}>
              <div className="w-1/3 text-sm font-medium truncate">{f.label}{f.required ? " *" : ""}</div>
              <select
                className="st-input flex-1"
                value={mapping[f.key] || ""}
                onChange={(e) => setMapping((p) => ({ ...p, [f.key]: e.target.value }))}
                data-testid={`csv-map-select-${f.key}`}
              >
                <option value="">— Don't import —</option>
                {parsed.extraHeaders.map((h) => (
                  <option key={h} value={h}>{h}{preview[h] ? ` (e.g. ${preview[h].slice(0, 20)})` : ""}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer" data-testid="csv-update-existing-toggle">
          <input type="checkbox" className="accent-[#dc2626] mt-0.5" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
          <span>Update people already on the list <span className="text-[#8a8a8a]">(fills in these fields for existing invitees instead of skipping them)</span></span>
        </label>
        <div className="flex justify-end gap-2 mt-6">
          <button className="st-btn st-btn-ghost" onClick={onCancel} data-testid="csv-map-cancel">Cancel</button>
          <button className="st-btn" onClick={() => onConfirm(mapping, updateExisting)} data-testid="csv-map-confirm">Import {parsed.records.length} invitees</button>
        </div>
      </div>
    </div>
  );
}
